import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import Button from 'components/ui/Button';
import Dropdown, { DropdownItem } from 'components/ui/Dropdown';
import { useUpdatePayment } from 'hooks/usePayments';
import { EstadoPago, MetodoPago, SessionPayment } from 'types/models';
import { MdCreditCard } from 'react-icons/md';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  payment: SessionPayment;
}

const METODO_ITEMS: DropdownItem[] = [
  { label: 'Efectivo',       value: MetodoPago.EFECTIVO },
  { label: 'Transferencia',  value: MetodoPago.TRANSFERENCIA },
  { label: 'Tarjeta',        value: MetodoPago.TARJETA },
  { label: 'Seguro médico',  value: MetodoPago.SEGURO },
];

const METODO_LABEL: Record<MetodoPago, string> = {
  [MetodoPago.EFECTIVO]:      'Efectivo',
  [MetodoPago.TRANSFERENCIA]: 'Transferencia',
  [MetodoPago.TARJETA]:       'Tarjeta',
  [MetodoPago.SEGURO]:        'Seguro médico',
};

export default function PaymentRegisterModal({ isOpen, onClose, payment }: Props) {
  const toast       = useToast();
  const updatePay   = useUpdatePayment();

  const [estadoPago,  setEstadoPago]  = useState<EstadoPago>(EstadoPago.PAGADO);
  const [metodoPago,  setMetodoPago]  = useState<MetodoPago>(MetodoPago.EFECTIVO);
  const [monto,       setMonto]       = useState(String(payment.monto));
  const [fechaPago,   setFechaPago]   = useState(() => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  });
  const [montoError, setMontoError]   = useState('');

  useEffect(() => {
    if (isOpen) {
      setMonto(String(payment.monto));
      setEstadoPago(payment.estadoPago === EstadoPago.PARCIAL ? EstadoPago.PAGADO : EstadoPago.PAGADO);
      setMetodoPago(payment.metodoPago ?? MetodoPago.EFECTIVO);
      setMontoError('');
    }
  }, [isOpen, payment]);

  const isParcial = payment.estadoPago === EstadoPago.PENDIENTE;

  const handleSubmit = async () => {
    const montoNum = parseFloat(monto);
    if (isNaN(montoNum) || montoNum <= 0) {
      setMontoError('Monto debe ser mayor a 0');
      return;
    }
    setMontoError('');

    try {
      await updatePay.mutateAsync({
        id: payment.id,
        payload: {
          estadoPago,
          metodoPago,
          monto: montoNum,
          fechaPago: new Date(fechaPago).toISOString(),
        },
      });
      toast({ title: 'Pago registrado', status: 'success', duration: 2500, isClosable: true, position: 'top-right' });
      onClose();
    } catch (err: any) {
      toast({
        title: 'Error al registrar pago',
        description: err?.response?.data?.message ?? 'Error inesperado',
        status: 'error', duration: 4000, isClosable: true, position: 'top-right',
      });
    }
  };

  const borderColor  = useColorModeValue('gray.200', 'whiteAlpha.200');
  const inputBg      = useColorModeValue('gray.50', 'navy.700');
  const labelColor   = useColorModeValue('gray.700', 'gray.200');
  const mutedColor   = useColorModeValue('gray.500', 'gray.400');
  const sectionColor = useColorModeValue('brand.500', 'brand.400');
  const chipBase    = { px: '14px', py: '7px', borderRadius: '20px', fontSize: 'xs', fontWeight: '700', cursor: 'pointer', border: '1.5px solid', transition: 'all 0.15s' };
  const chip = (active: boolean) => ({
    ...chipBase,
    bg: active ? 'brand.500' : 'transparent',
    borderColor: active ? 'brand.500' : borderColor,
    color: active ? 'white' : mutedColor,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.300" />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" gap="10px">
            <Flex w="36px" h="36px" bg="brand.500" borderRadius="10px" align="center" justify="center">
              <Icon as={MdCreditCard} color="white" w="18px" h="18px" />
            </Flex>
            <Text fontSize="md" fontWeight="800">Registrar pago</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody display="flex" flexDirection="column" gap="16px">
          {isParcial && (
            <Box>
              <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="wider" color={sectionColor} mb="10px">
                Tipo de pago
              </Text>
              <Flex gap="8px">
                <Box {...chip(estadoPago === EstadoPago.PAGADO)} onClick={() => setEstadoPago(EstadoPago.PAGADO)}>
                  Pago total
                </Box>
                <Box {...chip(estadoPago === EstadoPago.PARCIAL)} onClick={() => setEstadoPago(EstadoPago.PARCIAL)}>
                  Pago parcial
                </Box>
              </Flex>
            </Box>
          )}

          <FormControl isInvalid={!!montoError}>
            <FormLabel ms="4px" fontSize="sm" fontWeight="700" color={labelColor}>
              Monto (USD)
            </FormLabel>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              value={monto}
              onChange={e => { setMonto(e.target.value); setMontoError(''); }}
              bg={inputBg} border="1px solid" borderColor={montoError ? 'red.400' : borderColor}
              borderRadius="12px" fontSize="sm"
              _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
            />
            {montoError && <Text fontSize="xs" color="red.400" ms="4px" mt="4px">{montoError}</Text>}
          </FormControl>

          <FormControl>
            <FormLabel ms="4px" fontSize="sm" fontWeight="700" color={labelColor}>
              Método de pago
            </FormLabel>
            <Dropdown
              label={METODO_LABEL[metodoPago]}
              items={METODO_ITEMS}
              onSelect={val => setMetodoPago(val as MetodoPago)}
              w="100%"
              variant="outline"
              borderRadius="12px"
              menuWidth="100%"
            />
          </FormControl>

          <FormControl>
            <FormLabel ms="4px" fontSize="sm" fontWeight="700" color={labelColor}>
              Fecha de pago
            </FormLabel>
            <Input
              type="datetime-local"
              value={fechaPago}
              onChange={e => setFechaPago(e.target.value)}
              bg={inputBg} border="1px solid" borderColor={borderColor}
              borderRadius="12px" fontSize="sm"
              _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
            />
          </FormControl>
        </ModalBody>

        <ModalFooter gap="10px">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            colorScheme="brand"
            isLoading={updatePay.isPending}
            onClick={handleSubmit}
          >
            Confirmar pago
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
